import { useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { updateUserType } from '../utils/userType';
import styles from './HairstylistDetail.module.css';
import signupStyles from '../pages/SignupPage.module.css';
import instagramIcon from '../assets/images/logo_instagram.png';
import whatsappIcon from '../assets/images/logo_whatsapp.png';
import tiktokIcon from '../assets/images/tiktok_logo.png';
import linkIcon from '../assets/images/logo_personal_link.png';
import phoneIcon from '../assets/images/telephone_logo.png';
import StarRating from './StarRating';
import ReviewModal from './ReviewModal';
import { useReviews } from '../hooks/useReviews';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import AuthModal from './AuthModal';
import { logContactClick, logGalleryOpen } from '../utils/hairstylistAnalytics';
import { getDisplayLocation, getPrimaryCity, getCountry } from '../utils/locationHelpers';
import { getCountryLabel } from '../config/countries';
import { getCountryFlag } from '../utils/countryFlags';
import { DEFAULT_PROVIDER_TYPE } from '../config/providerTypes';

const EMPTY_ARRAY = [];

function HairstylistDetail({ stylist, serviceLookup = {}, categories = [], services = [] }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { reviewCount, reviews, userReview, deleteReview } = useReviews(stylist?.id);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [deletingReview, setDeletingReview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const reviewsSectionRef = useRef(null);
  const { t, language } = useTranslation();

  const contactFields = useMemo(() => [
    { key: 'instagram', labelKey: 'signup.contactFields.instagram', icon: instagramIcon },
    { key: 'whatsapp', labelKey: 'signup.contactFields.whatsapp', icon: whatsappIcon },
    { key: 'phone', labelKey: 'signup.contactFields.phone', icon: phoneIcon },
    { key: 'tiktok', labelKey: 'signup.contactFields.tiktok', icon: tiktokIcon },
    { key: 'externalLink', labelKey: 'signup.contactFields.externalLink', icon: linkIcon },
  ], []);

  const contactFieldsWithLabels = useMemo(() => contactFields.map((f) => ({ ...f, label: t(f.labelKey) })), [contactFields, t]);
  
  // Vérifier si l'utilisateur connecté est le propriétaire de ce profil
  // Un utilisateur ne peut pas noter son propre profil
  const isOwner = currentUser && stylist?.userId === currentUser.uid;
  
  // Vérifier si l'utilisateur est admin
  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
  const isAdminUser = currentUser && displayName?.toLowerCase() === 'admin';
  const stylistServices = stylist?.services || EMPTY_ARRAY;
  
  const servicesByCategory = useMemo(() => {
    if (!stylist) return [];
    return categories
      .map((category) => {
        // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
        let categoryLabel = category.label || '';
        if (language && language !== 'fr' && category.en) {
          categoryLabel = category.en;
        }
        
        const categoryServices = stylistServices
          .map((code) => services.find((s) => s.code === code))
          .filter(Boolean)
          .filter((service) => service.categoryCode === category.categoryCode)
          .map((service) => {
            // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
            let serviceLabel = service.label || service.prestation || '';
            if (language && language !== 'fr' && service.en) {
              serviceLabel = service.en;
            }
            return {
              ...service,
              label: serviceLabel,
            };
          });
        
        return {
          ...category,
          label: categoryLabel,
          services: categoryServices,
        };
      })
      .filter((cat) => cat.services.length > 0);
  }, [categories, services, stylist, stylistServices, language]);

  if (!stylist) {
    return (
      <div className={signupStyles.page}>
        <div className={signupStyles.wrapper}>
          <div className={styles.emptyState}>
            <p>{t('detail.emptyState')}</p>
          </div>
        </div>
      </div>
    );
  }

  const {
    name,
    cities = [],
    description,
    galleryPhotos = [],
    canTravel,
    identificationCode,
    contact,
    profilePhoto,
    accountType = 'PRO',
  } = stylist;

  const primaryCity = getPrimaryCity(stylist);
  const travelCities = cities.filter((city) => city && city !== primaryCity);
  const isPro = accountType === 'PRO';

  const contactFieldsWithValues = contactFieldsWithLabels
    .map((field) => ({
      ...field,
      value: contact?.[field.key] || stylist[field.key] || '',
    }))
    .filter((field) => field.value);

  const primaryContact = contact?.primaryContact || stylist.primaryContact || '';

  const handleOpenLightbox = (index) => {
    if (!galleryPhotos || galleryPhotos.length === 0) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
    
    // Log gallery open event
    logGalleryOpen(stylist.id, currentUser?.uid || null);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  const handlePrevLightbox = (event) => {
    event.stopPropagation();
    if (!galleryPhotos || galleryPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev - 1 + galleryPhotos.length) % galleryPhotos.length);
  };

  const handleNextLightbox = (event) => {
    event.stopPropagation();
    if (!galleryPhotos || galleryPhotos.length === 0) return;
    setLightboxIndex((prev) => (prev + 1) % galleryPhotos.length);
  };

  const handleDelete = async () => {
    if (!isAdminUser) return;
    
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le profil de ${name} ? Cette action est irréversible.`
    );
    
    if (!confirmed) return;
    
    setDeleting(true);
    try {
      // Get userId before deleting to update userType
      const userId = stylist.userId;
      
      // Delete the hairstylist profile
      await deleteDoc(doc(db, 'hairstylists', stylist.id));
      
      // Update userType to "client" if the profile had a userId
      if (userId) {
        try {
          await updateUserType(userId);
        } catch (userTypeError) {
          // eslint-disable-next-line no-console
          console.error('Error updating userType after profile deletion:', userTypeError);
          // Don't fail the deletion if userType update fails
        }
      }
      
      // Rediriger vers la page de recherche après suppression
      const providerType = stylist?.type || DEFAULT_PROVIDER_TYPE;
      navigate(`/search/${providerType}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting hairstylist:', error);
      alert(t('detail.errorDeleteProfile'));
      setDeleting(false);
    }
  };

  return (
    <div className={signupStyles.page}>
      <div className={signupStyles.wrapper}>
        <div className={signupStyles.form}>
          {/* Section 1: Informations générales */}
          <section className={signupStyles.card}>
            <div className={signupStyles.sectionHeader}>
              <div>
                <h2>{t('detail.generalInfo')}</h2>
              </div>
            </div>

            <div className={signupStyles.fieldGrid}>
              <div>
                <label>{t('detail.profileName')}</label>
                <div className={styles.nameRow}>
                  <div className={styles.readOnlyValue}>
                    {name || t('detail.notSpecified')}
                    {isPro && (
                      <svg className={styles.verifiedBadge} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12L11 14L15 10M12 2L15.09 5.26L19.18 5.63L18.81 9.72L22 12.77L18.81 15.82L19.18 19.91L15.09 20.28L12 23.5L8.91 20.28L4.82 19.91L5.19 15.82L2 12.77L5.19 9.72L4.82 5.63L8.91 5.26L12 2Z" fill="#B19CD9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  {isAdminUser && (
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={handleDelete}
                      disabled={deleting}
                      aria-label={t('detail.deleteProfile')}
                      title={t('detail.deleteProfile')}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {(() => {
                const countryCode = getCountry(stylist);
                return countryCode ? (
                  <div>
                    <label>{t('search.country')}</label>
                    <div className={styles.readOnlyValue}>
                      <span className={styles.countryFlag}>{getCountryFlag(countryCode)}</span>
                      {getCountryLabel(countryCode, language || 'fr')}
                    </div>
                  </div>
                ) : null;
              })()}
              <div>
                <label>{t('detail.mainCity')}</label>
                <div className={styles.readOnlyValue}>{getDisplayLocation(stylist)}</div>
              </div>
            </div>

            {canTravel && travelCities.length > 0 && (
              <div className={styles.readOnlySection}>
                <label>{t('detail.citiesServed')}</label>
                <div className={signupStyles.cityChips}>
                  {travelCities.map((city) => (
                    <span key={city} className={signupStyles.cityChip}>
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {description && (
              <div className={styles.readOnlySection}>
                <label>{t('detail.presentation')}</label>
                <div className={styles.descriptionValue}>{description}</div>
              </div>
            )}
          </section>

          {/* Section 2: Coordonnées */}
          {contactFieldsWithValues.length > 0 && (
            <section className={signupStyles.card}>
              <div className={signupStyles.sectionHeader}>
                <div>
                  <h2>{t('detail.coordinates')}</h2>
                </div>
              </div>

              <div className={styles.contactsContainer}>
                {contactFieldsWithValues.map((field) => {
                  const value = field.value;

                  const isLink = field.key === 'instagram' || field.key === 'externalLink';
                  const href =
                    field.key === 'instagram'
                      ? value.startsWith('http')
                        ? value
                        : `https://instagram.com/${value.replace('@', '')}`
                      : field.key === 'externalLink'
                        ? value.startsWith('http')
                          ? value
                          : `https://${value}`
                        : field.key === 'whatsapp'
                          ? `https://wa.me/${value.replace(/[^\d]/g, '')}`
                          : field.key === 'tiktok'
                            ? `https://www.tiktok.com/@${value.replace('@', '')}`
                            : field.key === 'phone'
                              ? `tel:${value.replace(/[^0-9+]/g, '')}`
                              : null;

                  if (isLink || href) {
                    // Lien sur toute la ligne, mais on garde exactement le style d'origine
                    return (
                      <a
                        key={field.key}
                        href={href || value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={signupStyles.contactField}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                        onClick={() => {
                          // Log contact click
                          logContactClick(stylist.id, field.key, currentUser?.uid || null);
                        }}
                      >
                        <span className={signupStyles.contactLabel}>
                          <img src={field.icon} alt={field.label} />
                          {field.label}
                        </span>
                        <span className={styles.readOnlyValue}>{value}</span>
                      </a>
                    );
                  }

                  return (
                    <div key={field.key} className={signupStyles.contactField}>
                      <span className={signupStyles.contactLabel}>
                        <img src={field.icon} alt={field.label} />
                        {field.label}
                      </span>
                      <div className={styles.readOnlyValue}>{value}</div>
                    </div>
                  );
                })}
              </div>

              {primaryContact && (
                <div className={signupStyles.primaryContactSection}>
                  <div className={signupStyles.primaryContactRow}>
                    <span className={signupStyles.primaryContactLabel}>
                      {t('detail.primaryContact')}
                    </span>
                    <div className={styles.readOnlyValue}>
                      {contactFieldsWithLabels.find((f) => f.key === primaryContact)?.label || primaryContact}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Section 3: Photos */}
          <section className={signupStyles.card}>
            <div className={signupStyles.sectionHeader}>
              <div>
                <h2>{t('detail.photos')}</h2>
              </div>
            </div>

            <div className={signupStyles.photoRow}>
              <div className={signupStyles.photoUpload}>
                {profilePhoto ? (
                  <img src={profilePhoto} alt={t('detail.profilePhoto')} />
                ) : (
                  <div className={styles.photoPlaceholder}>
                    <span>{t('detail.profilePhoto')}</span>
                  </div>
                )}
              </div>

              {galleryPhotos && galleryPhotos.length > 0 && (
                <div className={signupStyles.galleryUpload}>
                  <div className={signupStyles.galleryList}>
                    {galleryPhotos.map((photo, index) => (
                      <div
                        key={index}
                        className={signupStyles.galleryItem}
                        onClick={() => handleOpenLightbox(index)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleOpenLightbox(index);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={photo} alt={`${t('detail.galleryPhoto')} ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Section 4: Prestations */}
          {stylistServices.length > 0 && (
            <section className={signupStyles.card}>
              <div className={signupStyles.sectionHeader}>
                <div>
                  <h2>{t('detail.servicesOffered')}</h2>
                </div>
              </div>

              {servicesByCategory.map((category) => (
                <div key={category.categoryCode} className={signupStyles.accordion}>
                  <div className={signupStyles.accordionHeader} style={{ cursor: 'default' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span>{category.label}</span>
                    </div>
                  </div>
                  <div className={styles.servicesFlexContainer}>
                    {category.services.map((service) => (
                      <div key={service.code} className={styles.readOnlyService}>
                        <span>{serviceLookup[service.code] || service.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Section Reviews */}
          <section className={signupStyles.card} ref={reviewsSectionRef}>
            <div className={signupStyles.sectionHeader}>
              <div>
                <h2>{t('detail.reviews')} ({reviewCount})</h2>
              </div>
            </div>
            {/* Bouton donner mon avis - sous le titre sur mobile */}
            {!currentUser && (
              <div className={styles.mobileReviewButtonContainer}>
                <button
                  type="button"
                  className={styles.reviewBtn}
                  onClick={() => {
                    setShowAuthModal(true);
                  }}
                >
                  {t('detail.loginToReview')}
                </button>
              </div>
            )}
            {currentUser && !userReview && !isOwner && (
              <div className={styles.mobileReviewButtonContainer}>
                <button
                  type="button"
                  className={styles.reviewBtn}
                  onClick={() => {
                    setShowReviewModal(true);
                  }}
                >
                  {t('detail.giveReview')}
                </button>
              </div>
            )}
            <div className={styles.reviewsActions}>
              {/* Message si l'utilisateur n'est pas connecté */}
              {!currentUser && (
                <button
                  type="button"
                  className={`${styles.reviewBtn} ${styles.desktopReviewBtn}`}
                  onClick={() => {
                    setShowAuthModal(true);
                  }}
                >
                  {t('detail.loginToReview')}
                </button>
              )}
              {/* Bouton donner mon avis - desktop uniquement */}
              {currentUser && !userReview && !isOwner && (
                <button
                  type="button"
                  className={`${styles.reviewBtn} ${styles.desktopReviewBtn}`}
                  onClick={() => {
                    setShowReviewModal(true);
                  }}
                >
                  {t('detail.giveReview')}
                </button>
              )}
              {currentUser && userReview && !isOwner && (
                <div className={styles.userReviewActions}>
                  <button
                    type="button"
                    className={styles.editReviewBtn}
                    onClick={() => setShowReviewModal(true)}
                  >
                    {t('detail.editReview')}
                  </button>
                  <button
                    type="button"
                    className={styles.deleteReviewBtn}
                    onClick={async () => {
                      if (window.confirm(t('detail.confirmDeleteReview'))) {
                        setDeletingReview(true);
                        try {
                          await deleteReview();
                        } catch (error) {
                          // eslint-disable-next-line no-console
                          console.error('Error deleting review:', error);
                          alert(t('detail.errorDelete'));
                        } finally {
                          setDeletingReview(false);
                        }
                      }
                    }}
                    disabled={deletingReview}
                  >
                    {deletingReview ? t('detail.deleting') : t('detail.deleteReview')}
                  </button>
                </div>
              )}
            </div>
            {reviews && reviews.length > 0 && (
              <div className={styles.reviewsList}>
                {reviews.map((review) => (
                  <div key={review.id} className={styles.reviewItem}>
                    <div className={styles.reviewHeader}>
                      <div className={styles.reviewUser}>
                        <span className={styles.reviewUserName}>
                          {review.userName || t('detail.anonymousUser')}
                          {currentUser && review.userId === currentUser.uid && (
                            <span className={styles.yourReviewBadge}> {t('detail.yourReview')}</span>
                          )}
                        </span>
                        <div className={styles.reviewRating}>
                          <StarRating rating={review.rating} reviewCount={0} />
                        </div>
                      </div>
                      {review.createdAt && (
                        <span className={styles.reviewDate}>
                          {review.createdAt.toDate?.().toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'de' ? 'de-DE' : language === 'nl' ? 'nl-NL' : 'en-GB', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }) || t('detail.unknownDate')}
                        </span>
                      )}
                    </div>
                  {review.comment && (
                    <p className={styles.reviewComment}>{review.comment}</p>
                  )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Code d'identification en bas de page */}
          {identificationCode && (
            <div className={styles.identificationCodeFooter}>
              {t('detail.identificationCode')} {identificationCode}
            </div>
          )}
        </div>
      </div>
      {showReviewModal && (
        <ReviewModal
          hairstylistId={stylist.id}
          hairstylistName={name}
          existingReview={userReview}
          onClose={() => setShowReviewModal(false)}
        />
      )}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {lightboxOpen && galleryPhotos && galleryPhotos.length > 0 && (
        <div
          className={styles.lightboxOverlay}
          onClick={handleCloseLightbox}
          role="dialog"
          aria-modal="true"
        >
          <div
            className={styles.lightboxContent}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={handleCloseLightbox}
              aria-label={t('common.close')}
            >
              ×
            </button>
            {galleryPhotos.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
                  onClick={handlePrevLightbox}
                  aria-label={t('detail.prevPhoto')}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`${styles.lightboxNav} ${styles.lightboxNext}`}
                  onClick={handleNextLightbox}
                  aria-label={t('detail.nextPhoto')}
                >
                  ›
                </button>
              </>
            )}
            <img
              src={galleryPhotos[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1} sur ${galleryPhotos.length}`}
              className={styles.lightboxImage}
            />
            {galleryPhotos.length > 1 && (
              <div className={styles.lightboxCounter}>
                {lightboxIndex + 1} / {galleryPhotos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HairstylistDetail;
